import { api } from './client';

export interface AutocompletePrediction {
  placeId:       string;
  description:   string;
  mainText:      string;
  secondaryText: string;
}

export interface PlaceBasicDetails {
  placeId: string;
  name:    string;
  address: string;
  mapsUrl: string | null;
  lat:     number;
  lng:     number;
}

export interface OpeningHoursPeriod {
  open:   { day: number; hour: number; minute: number };
  close?: { day: number; hour: number; minute: number };
}

export const placesApi = {
  // `kind: 'destination'` narrows the results to cities, regions and countries
  // — what a trip destination actually is — instead of streets and businesses.
  autocomplete: (q: string, sessiontoken: string, opts: { bias?: string; kind?: 'destination' } = {}) =>
    api.get<{ predictions: AutocompletePrediction[] }>('/places/autocomplete', {
      params: { q, sessiontoken, ...(opts.bias ? { bias: opts.bias } : {}), ...(opts.kind ? { kind: opts.kind } : {}) },
    }).then(r => r.data.predictions),

  details: (placeId: string, sessiontoken?: string) =>
    api.get<{ details: PlaceBasicDetails | null }>('/places/details', {
      params: { placeId, ...(sessiontoken ? { sessiontoken } : {}) },
    }).then(r => r.data.details),

  // Resolve a Google photo resource name (`places/…/photos/…`) to a temporary
  // image URL. Charged as one Place Photo on the server (cache miss only), so
  // call it lazily — only for the photo actually on screen.
  // ADR-0005: the free pass. Returns only the photos the server already has in
  // its shared cache, so a card with several stops can paint what it has on
  // mount without buying a Place Photo per stop. Never bills — see
  // `resolveCachedPhotoUris`.
  cachedPhotos: (names: string[]) =>
    api.post<{ urls: Record<string, string> }>('/places/photos/cached', { names })
      .then(r => r.data.urls),

  photo: (name: string, tripId?: string) =>
    api.get<{ photoUri: string }>('/places/photo', {
      params: { name, ...(tripId ? { tripId } : {}) },
    }).then(r => r.data.photoUri),

  // Batched, DB-cached (30 days, global by placeId) opening-hours lookup —
  // never calls Google directly from the browser.
  // ADR-0005: cada placeId sin cachear son €0,020 (Place Details Enterprise).
  // Ninguna vista debe llamar a esto al montarse; el gesto lo da el usuario.
  openingHours: (placeIds: string[]) =>
    api.post<{ results: Record<string, OpeningHoursPeriod[] | null> }>(
      '/places/opening-hours',
      { placeIds },
    ).then(r => r.data.results),

  // Fire-and-forget: report a Google Maps JS map load so it lands in our usage
  // metrics (these are billed client-side and otherwise invisible to us).
  reportMapLoad: (tripId?: string) =>
    api.post('/places/map-load', tripId ? { tripId } : {}).catch(() => {}),
};
